import { Plugin } from '@ckeditor/ckeditor5-core';

type ImageStats = {
    count: number;
    totalBytes: number;
};

type PendingUploadStats = {
    count: number;
    totalBytes: number;
};

type UploadNotificationType = typeof TypeException[keyof typeof TypeException];
type UploadNotificationValue = Record<string, string | number | boolean | null | undefined>;

const TypeException = {
    WARNING: 'warning',
    DANGER: 'danger',
    SUCCESS: 'success'
} as const;


export default class ValidatePastedBase64ImagesPlugin extends Plugin {
    public static get pluginName() {
        return 'ValidatePastedBase64ImagesPlugin';
    }

    public init(): void {
        const editor = this.editor;

        const allowedMimeTypes = [
            'image/png',
            'image/jpeg',
            'image/gif'
        ];

        const maxImages = 20;
        const maxTotalBytes = 50 * 1024 * 1024;

        const fileRepository = editor.plugins.get('FileRepository');

        fileRepository.createUploadAdapter = (loader: any) => {
            return new ValidatingBase64UploadAdapter(loader, editor, {
                allowedMimeTypes,
                maxImages,
                maxTotalBytes
            });
        };
    }
}

class ValidatingBase64UploadAdapter {
    private static lastShownError: { message: string; at: number } | null = null;
    private static pendingUploadsByEditor = new WeakMap<object, PendingUploadStats>();

    private loader: any;
    private editor: any;
    private allowedMimeTypes: string[];
    private maxImages: number;
    private maxTotalBytes: number;

    constructor(
        loader: any,
        editor: any,
        options: {
            allowedMimeTypes: string[];
            maxImages: number;
            maxTotalBytes: number;
        }
    ) {
        this.loader = loader;
        this.editor = editor;
        this.allowedMimeTypes = options.allowedMimeTypes;
        this.maxImages = options.maxImages;
        this.maxTotalBytes = options.maxTotalBytes;
    }

    public async upload(): Promise<{ default: string | null }> {
        const file = await this.loader.file;

        if (!file) {
            this.rejectWithHandledWarning('NAO_FOI_POSSIVEL_LER_ARQUIVO_IMAGEM', {});
        }

        const mimeType = (file.type || '').toLowerCase();

        if (!this.allowedMimeTypes.includes(mimeType)) {
            this.rejectWithHandledWarning(
                'ANEXO_ARQUIVOS_SUPORTADOS_IMG',
                {}
            );
        }

        const uploadReservation = this.reserveUploadCapacity(file.size);

        try {
            const base64 = await readFileAsDataURL(file);

            return {
                default: base64
            };
        } finally {
            this.releaseUploadCapacity(uploadReservation);
        }
    }

    public abort(): void {
        // Sem necessidade especial neste caso.
    }

    private rejectWithHandledWarning(message: string, value: UploadNotificationValue, type: UploadNotificationType = TypeException.WARNING): never {
        this.showError(message, value, type);
        // Rejeita sem mensagem para nao gerar o warning duplicado padrao do CKEditor.
        throw '';
    }

    private showError(message: string, value: UploadNotificationValue, type?: UploadNotificationType): void {

        if (typeof window !== 'undefined') {
            const now = Date.now();
            const last = ValidatingBase64UploadAdapter.lastShownError;

            if (last && last.message === message && now - last.at < 1200) {
                return;
            }

            ValidatingBase64UploadAdapter.lastShownError = {
                message,
                at: now
            };

            //Habilita o callback via bridgers para chamar o toastr implementada no sistema
            const notify = this.editor?.config?.get('milvusUploadNotify');

            if (typeof notify === 'function') {
                try {
                    notify({ message, value, type });
                    return;
                } catch (error) {
                    console.error(error);
                }
            }

            const fallbackMessage = resolveUploadValidationMessage(message, value);

            if (typeof window.alert === 'function') {
                window.alert(fallbackMessage);
                return;
            }

            console.error({ message, value, type, fallbackMessage });
        }
    }

    private reserveUploadCapacity(fileSize: number): PendingUploadStats {
        const currentStats = getEditorImageStats(this.editor.getData());
        const pendingStats = ValidatingBase64UploadAdapter.pendingUploadsByEditor.get(this.editor) || { count: 0, totalBytes: 0 };

        const projectedCount = currentStats.count + pendingStats.count + 1;
        if (projectedCount > this.maxImages) {
            this.rejectWithHandledWarning(
                'QNT_ANEXOS_EXCEDIDO_CUSTOMIZAVEL',
                { maxImages: this.maxImages }
            );
        }

        const projectedTotalBytes = currentStats.totalBytes + pendingStats.totalBytes + fileSize;
        if (projectedTotalBytes > this.maxTotalBytes) {
            this.rejectWithHandledWarning(
                'ANEXO_TAMANHO_ARQUIVO_EMAIL_CUTOMIZAVEL',
                { maxTotalBytes: formatBytes(this.maxTotalBytes) }
            );
        }

        const updatedPendingStats = {
            count: pendingStats.count + 1,
            totalBytes: pendingStats.totalBytes + fileSize
        };

        ValidatingBase64UploadAdapter.pendingUploadsByEditor.set(this.editor, updatedPendingStats);

        return { count: 1, totalBytes: fileSize };
    }

    private releaseUploadCapacity(reservation: PendingUploadStats): void {
        const pendingStats = ValidatingBase64UploadAdapter.pendingUploadsByEditor.get(this.editor);

        if (!pendingStats) {
            return;
        }

        const nextPendingCount = Math.max(0, pendingStats.count - reservation.count);
        const nextPendingTotalBytes = Math.max(0, pendingStats.totalBytes - reservation.totalBytes);

        if (nextPendingCount === 0 && nextPendingTotalBytes === 0) {
            ValidatingBase64UploadAdapter.pendingUploadsByEditor.delete(this.editor);
            return;
        }

        ValidatingBase64UploadAdapter.pendingUploadsByEditor.set(this.editor, {
            count: nextPendingCount,
            totalBytes: nextPendingTotalBytes
        });
    }
}

/**
 * Conta todas as imagens do editor.
 * - Se for base64, soma o tamanho real estimado.
 * - Se for URL, conta na quantidade, mas nao no tamanho base64.
 */
function getEditorImageStats(html: string): ImageStats {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html || '', 'text/html');

    const images = Array.from(doc.querySelectorAll('img'));
    let count = 0;
    let totalBytes = 0;

    for (const img of images) {
        const src = (img.getAttribute('src') || '').trim();

        if (!src) {
            continue;
        }

        count++;
        const parsed = parseDataImageUrl(src);

        if (!parsed.ok) {
            continue;
        }

        const bytes = estimateBase64Bytes(parsed.base64Data);

        if (!Number.isNaN(bytes) && bytes > 0) {
            totalBytes += bytes;
        }
    }

    return { count, totalBytes };
}

function parseDataImageUrl(value: string):
    | { ok: true; mimeType: string; base64Data: string }
    | { ok: false } {
    const match = value.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,([A-Za-z0-9+/=\s]+)$/);

    if (!match) {
        return { ok: false };
    }

    const mimeType = match[1];
    const base64Data = match[2].replace(/\s/g, '');

    if (!/^[A-Za-z0-9+/=]+$/.test(base64Data)) {
        return { ok: false };
    }

    return {
        ok: true,
        mimeType,
        base64Data
    };
}

function estimateBase64Bytes(base64: string): number {
    const cleaned = base64.replace(/\s/g, '');

    if (!cleaned.length) {
        return 0;
    }

    const padding = cleaned.endsWith('==') ? 2 : cleaned.endsWith('=') ? 1 : 0;

    return Math.floor((cleaned.length * 3) / 4) - padding;
}

function readFileAsDataURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            if (typeof reader.result === 'string') {
                resolve(reader.result);
            } else {
                reject(new Error('Falha ao converter imagem para base64.'));
            }
        };

        reader.onerror = () => {
            reject(new Error('Erro ao ler o arquivo da imagem.'));
        };

        reader.readAsDataURL(file);
    });
}

function formatBytes(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = bytes;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex++;
    }

    return `${value.toFixed(unitIndex === 0 ? 0 : 2)} ${units[unitIndex]}`;
}

function detectUploadValidationLanguage(): 'pt' | 'en' | 'es' {
    const documentLanguage = typeof document !== 'undefined'
        ? (document.documentElement?.lang || document.body?.getAttribute('lang') || '')
        : '';
    const navigatorLanguage = typeof navigator !== 'undefined'
        ? ((navigator.languages && navigator.languages[0]) || navigator.language || '')
        : '';
    const language = `${documentLanguage} ${navigatorLanguage}`.toLowerCase();

    if (language.includes('es')) {
        return 'es';
    }

    if (language.includes('en')) {
        return 'en';
    }

    return 'pt';
}

function resolveUploadValidationMessage(message: string, value: UploadNotificationValue): string {
    const templatesByLanguage: Record<'pt' | 'en' | 'es', Record<string, string>> = {
        pt: {
            NAO_FOI_POSSIVEL_LER_ARQUIVO_IMAGEM: 'Nao foi possivel ler o arquivo da imagem.',
            ANEXO_ARQUIVOS_SUPORTADOS_IMG: 'Formato de imagem nao permitido. Use PNG, JPG/JPEG ou GIF.',
            QNT_ANEXOS_EXCEDIDO_CUSTOMIZAVEL: 'O limite total do editor e de {{maxImages}} imagens.',
            ANEXO_TAMANHO_ARQUIVO_EMAIL_CUTOMIZAVEL: 'O total de imagens no editor nao pode exceder {{maxTotalBytes}}.'
        },
        en: {
            NAO_FOI_POSSIVEL_LER_ARQUIVO_IMAGEM: 'Could not read the image file.',
            ANEXO_ARQUIVOS_SUPORTADOS_IMG: 'Image format not allowed. Use PNG, JPG/JPEG or GIF.',
            QNT_ANEXOS_EXCEDIDO_CUSTOMIZAVEL: 'The editor limit is {{maxImages}} images.',
            ANEXO_TAMANHO_ARQUIVO_EMAIL_CUTOMIZAVEL: 'The total image size in the editor cannot exceed {{maxTotalBytes}}.'
        },
        es: {
            NAO_FOI_POSSIVEL_LER_ARQUIVO_IMAGEM: 'No fue posible leer el archivo de la imagen.',
            ANEXO_ARQUIVOS_SUPORTADOS_IMG: 'Formato de imagen no permitido. Use PNG, JPG/JPEG o GIF.',
            QNT_ANEXOS_EXCEDIDO_CUSTOMIZAVEL: 'El limite total del editor es de {{maxImages}} imagenes.',
            ANEXO_TAMANHO_ARQUIVO_EMAIL_CUTOMIZAVEL: 'El total de imagenes en el editor no puede exceder {{maxTotalBytes}}.'
        }
    };

    const language = detectUploadValidationLanguage();
    const template = templatesByLanguage[language][message] || templatesByLanguage.pt[message] || message;

    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_fullMatch, token: string) => {
        const replacement = value[token];
        return replacement == null ? '' : String(replacement);
    });
}
